import {
    GET_OVERRIDES_LIST,
    GET_OVERRIDES_LIST_SUCCESS,
    GET_OVERRIDES_LIST_FAILURE,
    RESET_OVERRIDES_LIST,
    SET_OVERRIDES_SEARCH_PARAMS,
    CLEAR_OVERRIDES_SEARCH_PARAMS,
} from '../actions';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

const initialSearchParams = {
    account: '',
    approverName: '',
    requestorName: '',
    myCaseRef: '',
    requestDateStart: '',
    requestDateEnd: '',
    orderDateStart: '',
    orderDateEnd: '',
};

const initialState = {
    loading: false,
    overrideList: [],
    totalRecords: 0,
    badgeTotalRecords: 0,
    statusCode: '',
    statusMessage: '',
    filters: [],
    overrideFilterList: [],
    searchParams: { ...initialSearchParams },
    latestOrderDate: '',
    oldestOrderDate: '',
    latestOverrideRequestDate: '',
    oldestOverrideRequestDate: '',
};

const overridesListReducer = (state = initialState, action) => {
    switch (action.type) {
        case GET_OVERRIDES_LIST:
            return {
                ...state,
                loading: true,
            };
        case GET_OVERRIDES_LIST_SUCCESS: {
            // Merge incoming filter entries into existing list by filterType so concurrent
            // pendingMyAction and allOverrides responses never overwrite each other.
            // When the API returns no overrideFilterList (e.g. no records found), a synthetic
            // empty entry is created for the current filterType so stale counts are cleared to 0.
            const mergeFilterList = (existing, incoming, filterType) => {
                const normalizedIncoming = (incoming && incoming.length > 0)
                    ? incoming
                    : filterType
                        ? [{ filterType, overrideStatusList: [] }]
                        : [];
                const incomingTypes = new Set(normalizedIncoming.map((e) => e.filterType));
                return [...(existing || []).filter((e) => !incomingTypes.has(e.filterType)), ...normalizedIncoming];
            };
            if (action.filterOnly) {
                return {
                    ...state,
                    // badgeOnly calls update only the navbar badge; skip overrideFilterList to avoid race overwrite
                    ...(!action.badgeOnly && {
                        overrideFilterList: mergeFilterList(state.overrideFilterList, action.data.overrideFilterList, action.statusTypeFilter),
                    }),
                    ...(action.statusTypeFilter === 'pendingMyAction' && !action.isMyOverrides && !action.orderTypeFilter && {
                        badgeTotalRecords: action.data.totalNumberOfResults || 0,
                    }),
                };
            }
            // A pendingMyAction response scoped to a single order type only carries counts for
            // that type, so applying it would zero out the other My Action Required tabs.
            const isOrderTypeScopedMyAction = action.statusTypeFilter === 'pendingMyAction' &&
                Boolean(action.orderTypeFilter);
            const shouldUpdateFilterList = !isOrderTypeScopedMyAction && (
                action.statusTypeFilter === 'pendingMyAction' ||
                action.statusTypeFilter === 'allOverrides'
            );
            return {
                ...state,
                loading: false,
                overrideList: action.data.overrideOrdersList || [],
                totalRecords: action.data.totalNumberOfResults || 0,
                // The navbar badge shows every pending action, so ignore order-type scoped totals
                ...(action.statusTypeFilter === 'pendingMyAction' && !action.isMyOverrides && !action.orderTypeFilter && {
                    badgeTotalRecords: action.data.totalNumberOfResults || 0,
                }),
                filters: action.data.filters || [],
                ...(shouldUpdateFilterList && {
                    overrideFilterList: mergeFilterList(state.overrideFilterList, action.data.overrideFilterList, action.statusTypeFilter),
                }),
                statusCode: action.data.statusCode || '',
                statusMessage: action.data.statusMessage || '',
                latestOrderDate: action.data.overrideOrdersList?.length ? action.data.latestOrderDate || null : null,
                oldestOrderDate: action.data.overrideOrdersList?.length ? action.data.oldestOrderDate || null : null,
                latestOverrideRequestDate: action.data.overrideOrdersList?.length ? action.data.latestOverrideRequestDate || null : null,
                oldestOverrideRequestDate: action.data.overrideOrdersList?.length ? action.data.oldestOverrideRequestDate || null : null,
            };
        }
        case GET_OVERRIDES_LIST_FAILURE:
            return {
                ...state,
                loading: false,
                overrideList: [],
                totalRecords: 0,
                latestOrderDate: null,
                oldestOrderDate: null,
                latestOverrideRequestDate: null,
                oldestOverrideRequestDate: null,
                statusCode: action.data?.statusCode || '',
                statusMessage: action.data?.statusMessage || '',
            };
        case RESET_OVERRIDES_LIST:
            return {
                ...initialState,
                badgeTotalRecords: state.badgeTotalRecords,
            };
        case SET_OVERRIDES_SEARCH_PARAMS:
            return {
                ...state,
                searchParams: { ...state.searchParams, ...action.payload },
            };
        case CLEAR_OVERRIDES_SEARCH_PARAMS:
            return {
                ...state,
                searchParams: { ...initialSearchParams },
            };
        default:
            return state;
    }
};

export default persistReducer(
    {
        key: 'overridesListReducer',
        storage: storage,
        whitelist: ['searchParams'],
    },
    overridesListReducer
);
